import { useEffect, type RefObject } from 'react';
import { useViewerStore } from '@stores/viewerStore';
import { useAssetLibraryStore } from '@stores/assetLibraryStore';
import type { RendererController } from '@renderer/index';

const DRAG_THRESHOLD = 6;

/**
 * 场景点击拾取模型：当 asset panel 打开且非量测模式时，
 * canvas 点击触发 pickModel，命中则选中对应 asset。
 * 未命中模型时不取消选中，避免场景交互误触取消选择。
 * 拖拽操作（超出阈值）不触发选中，避免与相机拖拽冲突。
 */
export function useAssetPick(
  containerRef: RefObject<HTMLElement | null>,
  controllerRef: RefObject<RendererController | null>,
) {
  const panelOpen = useAssetLibraryStore((s) => s.panelOpen);
  const selectAsset = useAssetLibraryStore((s) => s.selectAssetFromPick);
  const measureMode = useViewerStore((s) => s.measureMode);

  useEffect(() => {
    if (!panelOpen || measureMode !== 'idle') return;
    const container = containerRef.current;
    if (!container) return;

    let downX = 0;
    let downY = 0;
    let isDown = false;

    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName !== 'CANVAS') return;
      isDown = true;
      downX = e.clientX;
      downY = e.clientY;
    };

    const onPointerUp = (e: PointerEvent) => {
      if (!isDown) return;
      isDown = false;

      const target = e.target as HTMLElement;
      if (target.tagName !== 'CANVAS') return;

      // 拖拽超过阈值 → 视为相机拖拽，不触发选中
      const dx = Math.abs(e.clientX - downX);
      const dy = Math.abs(e.clientY - downY);
      if (dx + dy > DRAG_THRESHOLD) return;

      // 延迟读取 controller，避免捕获到尚未初始化的实例
      const ctrl = controllerRef.current;
      if (!ctrl) return;

      // 仅命中模型时才选中；未命中不取消当前选中
      void ctrl.model.pickModel({ clientX: e.clientX, clientY: e.clientY }).then((result) => {
        if (result) {
          selectAsset(result.modelId);
        }
      });
    };

    container.addEventListener('pointerdown', onPointerDown);
    container.addEventListener('pointerup', onPointerUp);
    return () => {
      container.removeEventListener('pointerdown', onPointerDown);
      container.removeEventListener('pointerup', onPointerUp);
    };
  }, [panelOpen, measureMode, containerRef, controllerRef, selectAsset]);
}
